import React, { useState, useEffect, useCallback } from 'react';
import { API_CONFIG } from '../../../config/api';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Edit, Trash2, Eye, EyeOff, FileText, User, Calendar, Sparkles, ExternalLink, Send, Archive, RefreshCw, CheckCircle, XCircle, Clock, Info } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import MarkdownRenderer from '../../diagnosis/MarkdownRenderer';
import ConfirmDialog from '../../ui/ConfirmDialog';
import Toast from '../../ui/Toast';
import { useDialog } from '../../../hooks/useDialog';

interface NewsArticle {
  id: number;
  title: string;
  category?: string;
}

interface AnalysisTemplate {
  id: number;
  name: string;
}

interface OfficialAnalysis {
  id: number;
  article_id: number;
  template_id: number;
  author_id: number;
  content_json: any;
  full_article?: string;
  slug?: string;
  status: number;
  created_at: string;
  updated_at: string;
  // 关联数据
  article?: NewsArticle;
  template?: AnalysisTemplate;
}

interface FormData {
  article_id: number;
  template_id: number;
  content_json: string;
  full_article: string;
  slug: string;
  status: number;
}

const OfficialAnalysisManagement: React.FC = () => {
  const navigate = useNavigate();
  const { confirmDialog, showConfirm, closeConfirm, toast, showSuccess, showError, showWarning, closeToast } = useDialog();
  const [analyses, setAnalyses] = useState<OfficialAnalysis[]>([]);
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [templates, setTemplates] = useState<AnalysisTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // 分页相关状态
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10); // 每页显示10条
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateFormData, setGenerateFormData] = useState({
    article_id: 0,
    template_id: 0,
    auto_publish: false
  });
  // 批量操作相关状态
  const [selectedAnalyses, setSelectedAnalyses] = useState<Set<number>>(new Set());
  const [bulkGeneratingSlug, setBulkGeneratingSlug] = useState(false);
  const [bulkAuditing, setBulkAuditing] = useState(false);
  const [bulkRegenerating, setBulkRegenerating] = useState(false);
  const [auditResults, setAuditResults] = useState<Map<number, any>>(new Map());
  // 审核结果详情模态框状态
  const [showAuditDetailModal, setShowAuditDetailModal] = useState(false);
  const [selectedAuditResult, setSelectedAuditResult] = useState<any>(null);
  // 移除预览模态框相关状态，改为直接打开新页面

  useEffect(() => {
    fetchAnalyses();
    fetchArticles();
    fetchTemplates();
    fetchAuditHistory();
  }, []);

  // 防抖搜索
  const [searchDebounce, setSearchDebounce] = useState<NodeJS.Timeout | null>(null);
  
  // 当页码改变时立即获取数据
  useEffect(() => {
    fetchAnalyses();
    fetchAuditHistory();
    // 清空选择状态
    setSelectedAnalyses(new Set());
  }, [currentPage]);

  // 当搜索条件或状态筛选改变时，重置到第一页并防抖获取数据
  useEffect(() => {
    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }
    
    const timeout = setTimeout(() => {
      // 清空选择状态
      setSelectedAnalyses(new Set());
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchAnalyses();
        fetchAuditHistory();
      }
    }, 500); // 500ms防抖
    
    setSearchDebounce(timeout);
    
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [searchTerm, statusFilter]);

  const fetchAnalyses = async () => {
    try {
      setLoading(true);
      
      // 构建查询参数
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString()
      });
      
      // 添加搜索和筛选参数
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=analysis&${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // 映射数据结构到前端期望的结构
        const analysesData = result.data?.data || result.data || [];
        const mappedAnalyses = Array.isArray(analysesData) ? analysesData.map((analysis: any) => ({
          ...analysis,
          article: analysis.insight_news_article,
          template: analysis.insight_analysis_template
        })) : [];
        setAnalyses(mappedAnalyses);
        
        // 设置分页信息
        if (result.data?.pagination) {
          setTotalPages(result.data.pagination.totalPages || 1);
          setTotalCount(result.data.pagination.totalCount || 0);
        } else {
          // 如果没有分页信息，假设只有一页
          setTotalPages(1);
          setTotalCount(mappedAnalyses.length);
        }
      } else {
        console.error('获取官方深度分析失败:', result.error);
        setAnalyses([]); // 确保设置为空数组
        setTotalPages(1);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('获取官方深度分析失败:', error);
      setAnalyses([]); // 确保设置为空数组
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchArticles = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=articles&action=list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          page: 1,
          limit: 1000,
          status: '1' // 只获取已发布的文章
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        const articlesData = result.data?.data || result.data || [];
        setArticles(Array.isArray(articlesData) ? articlesData : []);
      } else {
        console.error('获取新闻文章失败:', result.error);
        setArticles([]); // 确保设置为空数组
      }
    } catch (error) {
      console.error('获取新闻文章失败:', error);
      setArticles([]); // 确保设置为空数组
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=templates&action=list&status=1`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        const templatesData = result.data?.data || result.data || [];
        setTemplates(Array.isArray(templatesData) ? templatesData : []);
      } else {
        console.error('获取分析模板失败:', result.error);
        setTemplates([]); // 确保设置为空数组
      }
    } catch (error) {
      console.error('获取分析模板失败:', error);
      setTemplates([]); // 确保设置为空数组
    }
  };

  const fetchAuditHistory = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/audit-admin-manage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'get_audit_history'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data.history) {
        // 构建审核结果映射，使用最新的审核记录
        const newAuditResults = new Map();
        
        // 按target_type和target_id分组，只保留最新的审核记录
        const auditMap = new Map();
        result.data.history.forEach((record: any) => {
          if (record.target_type === 'official_analysis') {
            const key = record.target_id;
            const existing = auditMap.get(key);
            if (!existing || new Date(record.created_at) > new Date(existing.created_at)) {
              auditMap.set(key, record);
            }
          }
        });

        // 转换为前端期望的格式
        auditMap.forEach((record, targetId) => {
          newAuditResults.set(parseInt(targetId), {
            target_id: targetId,
            audit_status: record.audit_status,
            score: record.ai_score || 0,
            reason: record.audit_reason || '无具体原因',
            audit_result: record.audit_result
          });
        });

        setAuditResults(newAuditResults);
        console.log('✅ 审核历史加载完成，共', newAuditResults.size, '条记录');
      }
    } catch (error) {
      console.error('获取审核历史失败:', error);
    }
  };



  const handleDelete = async (id: number) => {
    showConfirm({
      title: '删除确认',
      message: '确定要删除这个官方深度分析吗？此操作无法撤销。',
      type: 'danger',
      confirmText: '删除',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=analysis&id=${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          if (!result.success) {
            console.error('删除官方深度分析失败:', result.error);
            showError('删除失败：' + result.error);
            return;
          }

          await fetchAnalyses();
          await fetchAuditHistory();
          showSuccess('官方深度分析删除成功！');
        } catch (error) {
          console.error('删除官方深度分析失败:', error);
          showError('删除失败，请重试');
        }
      }
    });
  };

  // 一键生成官方深度分析
  const handleGenerate = async () => {
    if (!generateFormData.article_id) {
      showWarning('请选择新闻文章');
      return;
    }

    try {
      setGenerating(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/aimodule-content-admin-generate-news`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'generate',
          type: 'official_analysis',
          parameters: {
            articleId: generateFormData.article_id.toString(),
            templateId: generateFormData.template_id > 0 ? generateFormData.template_id.toString() : undefined,
            auto_publish: generateFormData.auto_publish
          }
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showSuccess(
          `官方深度分析生成成功！使用模板：${result.data.selected_template_name}，状态：${result.data.status === 1 ? '已发布' : '草稿'}`,
          '生成成功'
        );
        setShowGenerateForm(false);
        setGenerateFormData({ article_id: 0, template_id: 0, auto_publish: false });
        await fetchAnalyses();
        await fetchAuditHistory();
      } else {
        showError('生成失败：' + (result.error || '未知错误'));
      }
    } catch (error) {
      console.error('生成官方深度分析失败:', error);
      showError('生成失败，请重试');
    } finally {
      setGenerating(false);
    }
  };

  // 获取可用的新闻文章（排除已有官方分析的）
  const getAvailableArticles = async () => {
    try {
      // 获取所有已发布的文章
      const articlesResponse = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=articles&action=list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          page: 1,
          limit: 1000,
          status: '1'
        })
      });

      if (!articlesResponse.ok) {
        throw new Error(`HTTP error! status: ${articlesResponse.status}`);
      }

      const articlesResult = await articlesResponse.json();
      const allArticles = articlesResult.success ? (articlesResult.data.data || []) : [];

      // 获取已有官方分析的文章
      const analysesResponse = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=analysis&page=1&limit=1000`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!analysesResponse.ok) {
        throw new Error(`HTTP error! status: ${analysesResponse.status}`);
      }

      const analysesResult = await analysesResponse.json();
      const usedArticles = analysesResult.success ? (analysesResult.data.data || []) : [];

      const usedArticleIds = new Set(usedArticles.map((a: any) => a.article_id) || []);
      const availableArticles = allArticles.filter((article: any) => !usedArticleIds.has(article.id)) || [];
      
      return availableArticles;
    } catch (error) {
      console.error('获取可用文章失败:', error);
      return [];
    }
  };

  // 打开生成表单时获取可用文章
  const handleOpenGenerateForm = async () => {
    const availableArticles = await getAvailableArticles();
    if (availableArticles.length === 0) {
      alert('暂无可用的新闻文章，所有文章都已生成官方深度分析');
      return;
    }
    // 更新articles状态为可用文章
    setArticles(availableArticles);
    setShowGenerateForm(true);
  };

  const handleEdit = (analysis: OfficialAnalysis) => {
    navigate(`/admin/insights/edit/${analysis.id}`);
  };

  const handlePreview = (analysis: OfficialAnalysis) => {
    // 直接在新窗口打开预览页面
    const previewUrl = analysis.slug 
      ? `/official-analysis/${analysis.slug}` 
      : `/official-analysis-id/${analysis.id}`;
    window.open(previewUrl, '_blank');
  };

  // 发布/取消发布官方深度分析
  const handlePublish = async (id: number, currentStatus: number) => {
    const isPublished = currentStatus === 1;
    const action = isPublished ? '取消发布' : '发布';
    
    showConfirm({
      title: `${action}确认`,
      message: `确定要${action}这个官方深度分析吗？`,
      type: isPublished ? 'warning' : 'info',
      confirmText: action,
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=analysis&action=update_analysis`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              id: id.toString(),
              updateData: {
                status: isPublished ? 0 : 1
              }
            })
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          if (!result.success) {
            console.error(`${action}官方深度分析失败:`, result.error);
            showError(`${action}失败：` + result.error);
            return;
          }

          await fetchAnalyses();
          await fetchAuditHistory();
          showSuccess(`${action}成功！`);
        } catch (error) {
          console.error(`${action}官方深度分析失败:`, error);
          showError(`${action}失败，请重试`);
        }
      }
    });
  };

  // 批量操作：全选/取消全选
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredAnalyses.map(analysis => analysis.id));
      setSelectedAnalyses(allIds);
    } else {
      setSelectedAnalyses(new Set());
    }
  };

  // 批量操作：单个选择
  const handleSelectSingle = (id: number, checked: boolean) => {
    const newSelected = new Set(selectedAnalyses);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedAnalyses(newSelected);
  };

  // 批量生成slug
  const handleBulkGenerateSlug = async () => {
    if (selectedAnalyses.size === 0) {
      showWarning('请先选择要生成slug的分析');
      return;
    }

    showConfirm({
      title: '批量生成Slug确认',
      message: `确定要为选中的 ${selectedAnalyses.size} 个分析生成slug吗？`,
      type: 'info',
      confirmText: '开始生成',
      onConfirm: async () => {
        try {
          setBulkGeneratingSlug(true);
          let successCount = 0;
          let errorCount = 0;
          const errors: string[] = [];

          // 获取选中的分析数据
          const selectedAnalysisData = filteredAnalyses.filter(analysis => 
            selectedAnalyses.has(analysis.id)
          );

          for (const analysis of selectedAnalysisData) {
            try {
              // 调用生成slug的API
              const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/aimodule-content-admin-generate-news`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  action: 'generate',
                  type: 'slug',
                  parameters: {
                    title: analysis.article?.title || `分析 ${analysis.id}`,
                    content: analysis.full_article || ''
                  }
                })
              });

              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              const result = await response.json();
              if (result.success && result.data?.slug) {
                // 更新分析记录的slug
                const updateResponse = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=analysis&action=update_analysis`, {
                  method: 'PUT',
                  headers: {
                    'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    id: analysis.id.toString(),
                    updateData: {
                      slug: result.data.slug
                    }
                  })
                });

                if (updateResponse.ok) {
                  successCount++;
                } else {
                  errorCount++;
                  errors.push(`分析ID ${analysis.id}: 更新失败`);
                }
              } else {
                errorCount++;
                errors.push(`分析ID ${analysis.id}: ${result.error || '生成失败'}`);
              }
            } catch (error) {
              errorCount++;
              errors.push(`分析ID ${analysis.id}: ${(error as Error).message}`);
            }
          }

          // 刷新列表
          await fetchAnalyses();
          await fetchAuditHistory();
          
          // 清空选择
          setSelectedAnalyses(new Set());

          // 显示结果
          if (successCount > 0 && errorCount === 0) {
            showSuccess(`批量生成slug成功！共处理 ${successCount} 个分析`);
          } else if (successCount > 0 && errorCount > 0) {
            showWarning(`部分成功：${successCount} 个成功，${errorCount} 个失败。失败详情：${errors.join('; ')}`);
          } else {
            showError(`批量生成失败：${errors.join('; ')}`);
          }
        } catch (error) {
          console.error('批量生成slug失败:', error);
          showError('批量生成失败，请重试');
        } finally {
          setBulkGeneratingSlug(false);
        }
      }
    });
  };

  // 批量审核功能
  const handleBulkAudit = async () => {
    if (selectedAnalyses.size === 0) {
      showWarning('请先选择要审核的分析');
      return;
    }

    showConfirm({
      title: 'AI批量审核确认',
      message: `确定要对选中的 ${selectedAnalyses.size} 个深度分析进行AI审核吗？审核通过的文章将自动发布。`,
      type: 'info',
      confirmText: '开始审核',
      onConfirm: async () => {
        try {
          setBulkAuditing(true);
          
          // 准备审核目标
          const targets = Array.from(selectedAnalyses).map(id => ({
            type: 'official_analysis' as const,
            id: id.toString()
          }));

          // 调用审核API
          const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/audit-admin-manage`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              action: 'batch_audit',
              targets
            })
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          
          if (result.success) {
            // 保存审核结果
            const newAuditResults = new Map(auditResults);
            result.data.results.forEach((auditResult: any) => {
              // 处理不同类型的ID，确保与analysis.id类型一致
              const targetId = parseInt(auditResult.target_id);
              newAuditResults.set(targetId, auditResult);
            });
            setAuditResults(newAuditResults);

            // 刷新列表
            await fetchAnalyses();
            await fetchAuditHistory();
            
            // 清空选择
            setSelectedAnalyses(new Set());

            // 显示结果统计
            const { summary } = result.data;
            if (summary.approved > 0 && summary.rejected === 0) {
              showSuccess(`AI审核完成！全部 ${summary.approved} 个分析审核通过并已发布`);
            } else if (summary.approved > 0 && summary.rejected > 0) {
              showWarning(`AI审核完成！${summary.approved} 个通过并发布，${summary.rejected} 个被驳回`);
            } else {
              showError(`AI审核完成！全部 ${summary.rejected} 个分析被驳回`);
            }
          } else {
            showError('批量审核失败：' + (result.error || '未知错误'));
          }
        } catch (error) {
          console.error('批量审核失败:', error);
          showError('批量审核失败，请重试');
        } finally {
          setBulkAuditing(false);
        }
      }
    });
  };

  // 打开审核详情弹窗
  const handleViewAuditDetail = (analysisId: number) => {
    const auditResult = auditResults.get(analysisId);
    if (auditResult) {
      setSelectedAuditResult(auditResult);
      setShowAuditDetailModal(true);
    }
  };

  // 批量AI重新生成功能
  const handleBulkRegenerate = async () => {
    if (selectedAnalyses.size === 0) {
      showWarning('请先选择要重新生成的分析');
      return;
    }

    showConfirm({
      title: 'AI批量重新生成确认',
      message: `确定要对选中的 ${selectedAnalyses.size} 个深度分析进行AI重新生成吗？这将覆盖现有内容。`,
      type: 'warning',
      confirmText: '开始重新生成',
      onConfirm: async () => {
        try {
          setBulkRegenerating(true);
          let successCount = 0;
          let errorCount = 0;
          const errors: string[] = [];

          // 获取选中的分析数据
          const selectedAnalysisData = filteredAnalyses.filter(analysis => 
            selectedAnalyses.has(analysis.id)
          );

          for (const analysis of selectedAnalysisData) {
            try {
              // 验证必要参数
              if (!analysis.article_id || !analysis.template_id) {
                errorCount++;
                errors.push(`分析ID ${analysis.id}: 数据不完整，无法重新生成`);
                continue;
              }

              // 调用AI重新生成API
              const functionsUrl = API_CONFIG.FUNCTIONS_URL || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
              const response = await fetch(`${functionsUrl}/aimodule-content-admin-generate-news`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  action: 'generate',
                  type: 'official_analysis',
                  parameters: {
                    articleId: analysis.article_id.toString(),
                    templateId: analysis.template_id.toString(),
                    auto_publish: false
                  }
                })
              });

              const result = await response.json();

              if (response.ok && result.success) {
                successCount++;
              } else {
                errorCount++;
                errors.push(`分析ID ${analysis.id}: ${result.error || '生成失败'}`);
              }
            } catch (error) {
              errorCount++;
              errors.push(`分析ID ${analysis.id}: ${(error as Error).message}`);
            }
          }

          // 刷新列表
          await fetchAnalyses();
          await fetchAuditHistory();
          
          // 清空选择
          setSelectedAnalyses(new Set());

          // 显示结果
          if (successCount > 0 && errorCount === 0) {
            showSuccess(`批量AI重新生成成功！共处理 ${successCount} 个分析`);
          } else if (successCount > 0 && errorCount > 0) {
            showWarning(`部分成功：${successCount} 个成功，${errorCount} 个失败。失败详情：${errors.join('; ')}`);
          } else {
            showError(`批量重新生成失败：${errors.join('; ')}`);
          }
        } catch (error) {
          console.error('批量AI重新生成失败:', error);
          showError('批量重新生成失败，请重试');
        } finally {
          setBulkRegenerating(false);
        }
      }
    });
  };

  // 获取审核状态显示
  const getAuditStatusBadge = (analysisId: number) => {
    const auditResult = auditResults.get(analysisId);
    if (!auditResult) return null;

    const { audit_status, score, reason } = auditResult;
    
    // 根据评分判断真实的审核状态
    const actualScore = score * 100; // 转换为百分制
    const isActuallyApproved = actualScore >= 90;
    
    // 如果正在处理中，显示处理状态
    if (audit_status === 'processing') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          审核中
        </span>
      );
    }
    
    // 根据实际评分显示状态
    if (isActuallyApproved) {
      return (
        <div className="flex items-center gap-1">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            审核通过
          </span>
          <button
            onClick={() => handleViewAuditDetail(analysisId)}
            className="inline-flex items-center text-green-600 hover:text-green-800 p-1 rounded-full hover:bg-green-50"
            title="查看审核详情"
          >
            <Info className="w-3 h-3" />
          </button>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            审核驳回
          </span>
          <button
            onClick={() => handleViewAuditDetail(analysisId)}
            className="inline-flex items-center text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50"
            title="查看审核详情"
          >
            <Info className="w-3 h-3" />
          </button>
        </div>
      );
    }
  };





  // 由于现在在后端进行筛选，这里直接使用analyses数据
  const filteredAnalyses = Array.isArray(analyses) ? analyses : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">官方深度分析管理</h2>
        </div>
        <div className="p-6">
          {/* 搜索和筛选 */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索文章标题或模板名称..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部状态</option>
              <option value="1">已发布</option>
              <option value="0">草稿</option>
            </select>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  fetchAnalyses();
                  fetchAuditHistory();
                }}
                disabled={loading}
                className="bg-gray-600 text-white px-3 py-1.5 rounded-md hover:bg-gray-700 flex items-center gap-1.5 transition-all duration-200 disabled:opacity-50 text-sm"
                title="刷新列表"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                    刷新中
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3 h-3" />
                    刷新
                  </>
                )}
              </button>
              {selectedAnalyses.size > 0 && (
                <>
                  <button
                    onClick={handleBulkGenerateSlug}
                    disabled={bulkGeneratingSlug || bulkAuditing || bulkRegenerating}
                    className="bg-gradient-to-r from-green-600 to-teal-600 text-white px-3 py-1.5 rounded-md hover:from-green-700 hover:to-teal-700 flex items-center gap-1.5 transition-all duration-200 disabled:opacity-50 text-sm"
                  >
                    {bulkGeneratingSlug ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        生成中
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-3 h-3" />
                        生成Slug ({selectedAnalyses.size})
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleBulkRegenerate}
                    disabled={bulkRegenerating || bulkAuditing || bulkGeneratingSlug}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-3 py-1.5 rounded-md hover:from-purple-700 hover:to-indigo-700 flex items-center gap-1.5 transition-all duration-200 disabled:opacity-50 text-sm"
                  >
                    {bulkRegenerating ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        重新生成中
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-3 h-3" />
                        AI重新生成 ({selectedAnalyses.size})
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleBulkAudit}
                    disabled={bulkAuditing || bulkGeneratingSlug || bulkRegenerating}
                    className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-3 py-1.5 rounded-md hover:from-orange-700 hover:to-red-700 flex items-center gap-1.5 transition-all duration-200 disabled:opacity-50 text-sm"
                  >
                    {bulkAuditing ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        审核中
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-3 h-3" />
                        AI审核 ({selectedAnalyses.size})
                      </>
                    )}
                  </button>
                </>
              )}
              <button
                onClick={handleOpenGenerateForm}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 py-1.5 rounded-md hover:from-purple-700 hover:to-blue-700 flex items-center gap-1.5 transition-all duration-200 text-sm"
              >
                <Sparkles className="w-3 h-3" />
                AI生成
              </button>
              <button
                onClick={() => navigate('/admin/insights/edit/new')}
                className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm"
              >
                <Plus className="w-3 h-3 mr-1.5" />
                手动添加
              </button>
            </div>
          </div>

          {/* 分析列表 */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedAnalyses.size === filteredAnalyses.length && filteredAnalyses.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    文章标题
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    分析模板
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Slug
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    审核状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    创建时间
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAnalyses.map((analysis) => (
                  <tr key={analysis.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedAnalyses.has(analysis.id)}
                        onChange={(e) => handleSelectSingle(analysis.id, e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 mr-2 text-blue-500" />
                        <div>
                          <div className="font-medium">{analysis.article?.title || `文章ID: ${analysis.article_id}`}</div>
                          {analysis.article?.category && (
                            <div className="text-xs text-gray-500">{analysis.article.category}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-2 text-green-500" />
                        {analysis.template?.name || `模板ID: ${analysis.template_id}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="max-w-xs">
                        {analysis.slug ? (
                          <span 
                            className="text-green-600 font-mono text-xs truncate block cursor-pointer" 
                            title={analysis.slug}
                          >
                            {analysis.slug}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">未设置slug</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        analysis.status === 1 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {analysis.status === 1 ? (
                          <><Eye className="w-3 h-3 mr-1" />已发布</>
                        ) : (
                          <><EyeOff className="w-3 h-3 mr-1" />草稿</>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getAuditStatusBadge(analysis.id) || (
                        <span className="text-gray-400 text-xs">未审核</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(analysis.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePreview(analysis)}
                          className="text-green-600 hover:text-green-900"
                          title="预览"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handlePublish(analysis.id, analysis.status)}
                          className={`${analysis.status === 1 ? 'text-orange-600 hover:text-orange-900' : 'text-purple-600 hover:text-purple-900'}`}
                          title={analysis.status === 1 ? '取消发布' : '发布'}
                        >
                          {analysis.status === 1 ? <Archive className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleEdit(analysis)}
                          className="text-blue-600 hover:text-blue-900"
                          title="编辑"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(analysis.id)}
                          className="text-red-600 hover:text-red-900"
                          title="删除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 分页组件 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-200">
              <div className="flex items-center text-sm text-gray-700">
                <span>
                  显示第 {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} 条，
                  共 {totalCount} 条记录
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  上一页
                </button>
                
                {/* 页码按钮 */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  下一页
                </button>
              </div>
            </div>
          )}

          {filteredAnalyses.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-gray-500">暂无官方深度分析数据</p>
            </div>
          )}
        </div>
      </div>

      {/* AI生成表单弹窗 */}
      {showGenerateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
                AI一键生成官方深度分析
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    选择新闻文章 *
                  </label>
                  <select
                    required
                    value={generateFormData.article_id}
                    onChange={(e) => setGenerateFormData({ ...generateFormData, article_id: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value={0}>请选择文章</option>
                    {Array.isArray(articles) && articles.map((article) => (
                      <option key={article.id} value={article.id}>
                        {article.title}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    分析模板（可选）
                  </label>
                  <select
                    value={generateFormData.template_id}
                    onChange={(e) => setGenerateFormData({ ...generateFormData, template_id: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value={0}>自动选择最佳模板</option>
                    {Array.isArray(templates) && templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">留空将由AI自动选择最适合的分析模板</p>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="auto_publish"
                    checked={generateFormData.auto_publish}
                    onChange={(e) => setGenerateFormData({ ...generateFormData, auto_publish: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="auto_publish" className="ml-2 block text-sm text-gray-900">
                    生成后自动发布
                  </label>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <Sparkles className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        AI生成说明
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>AI将根据新闻文章内容和选定的模板，自动生成专业的深度分析报告，包括：</p>
                        <ul className="list-disc list-inside mt-1 space-y-1">
                          <li>事件背景分析</li>
                          <li>深度解读与影响评估</li>
                          <li>趋势预测与建议</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowGenerateForm(false);
                      setGenerateFormData({ article_id: 0, template_id: 0, auto_publish: false });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleGenerate}
                    disabled={generating || !generateFormData.article_id}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-md hover:from-purple-700 hover:to-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex items-center"
                  >
                    {generating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        生成中...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        开始生成
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 审核详情弹窗 */}
      {showAuditDetailModal && selectedAuditResult && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Info className="w-5 h-5 mr-2 text-blue-600" />
                  审核结果详情
                </h3>
                <button
                  onClick={() => setShowAuditDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* 审核状态 */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">审核状态</h4>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      selectedAuditResult.audit_status === 'approved'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedAuditResult.audit_status === 'approved' ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          审核通过
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 mr-1" />
                          审核驳回
                        </>
                      )}
                    </span>
                  </div>
                  
                  {/* 评分 */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">AI评分</label>
                    <div className="flex items-center">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                        <div 
                          className={`h-2 rounded-full ${
                            selectedAuditResult.score * 100 >= 90 
                              ? 'bg-green-500' 
                              : selectedAuditResult.score * 100 >= 70
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(selectedAuditResult.score * 100, 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 min-w-[3rem]">
                        {Math.round(selectedAuditResult.score * 100)}/100
                      </span>
                    </div>
                  </div>
                </div>

                {/* 审核原因 */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    {selectedAuditResult.audit_status === 'approved' ? '通过原因' : '驳回原因'}
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {selectedAuditResult.reason || '暂无详细说明'}
                    </p>
                  </div>
                </div>

                {/* 如果有详细的审核结果数据，显示更多信息 */}
                {selectedAuditResult.audit_result && typeof selectedAuditResult.audit_result === 'object' && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">详细分析</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                        {JSON.stringify(selectedAuditResult.audit_result, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowAuditDetailModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 预览功能已改为直接在新页面打开 */}

      {/* 确认对话框 */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        loading={confirmDialog.loading}
        onConfirm={confirmDialog.onConfirm}
        onClose={closeConfirm}
      />

      {/* 通知提示 */}
      <Toast
        isOpen={toast.isOpen}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        duration={toast.duration}
        onClose={closeToast}
      />
    </div>
  );
};

export default OfficialAnalysisManagement;